import { useMemo } from 'react'
import type {
    ChecklistItem,
    ItemScore,
    MaintenanceEvent,
    SurveyAnswer,
} from '~/api/types'
import type { ImpactRow } from './types'
import { buildResultItems } from './impact-result'

type UseMaintenanceImpactParams = {
    activeEvent: MaintenanceEvent | null
    checklist: ChecklistItem[]
    answers: SurveyAnswer[]
    scores: ItemScore[]
    selectedImpactId: string | null
}

export function useMaintenanceImpact({
    activeEvent,
    checklist,
    answers,
    scores,
    selectedImpactId,
}: UseMaintenanceImpactParams) {
    const resultItems = useMemo(
        () => buildResultItems(activeEvent?.impact.affected ?? [], checklist),
        [activeEvent, checklist]
    )

    const impactRows = useMemo<ImpactRow[]>(() => {
        const affectedById = new Map(
            resultItems.map((item) => [item.item_id, item])
        )
        const scoreById = new Map(scores.map((score) => [score.itemId, score]))
        return checklist.map((item) => ({
            item,
            affected: affectedById.get(item.id),
            score: scoreById.get(item.id),
            answers: answers.filter((answer) =>
                item.fields.some((field) => field.fieldId === answer.fieldId)
            ),
        }))
    }, [answers, checklist, resultItems, scores])

    const recheckCount = resultItems.filter(
        (item) => item.recheck_required
    ).length
    const keepCount = Math.max(resultItems.length - recheckCount, 0)
    const unaffectedCount = Math.max(checklist.length - resultItems.length, 0)
    const selectedImpact =
        impactRows.find((row) => row.item.id === selectedImpactId) ??
        impactRows.find((row) => row.affected) ??
        null

    return {
        resultItems,
        impactRows,
        recheckCount,
        keepCount,
        unaffectedCount,
        selectedImpact,
    }
}
